import type { BrushId, Point } from "@canvas/contracts";

export interface BrushMark {
  element: "circle" | "path";
  pointIndex: number;
  attributes: Readonly<Record<string, string>>;
}

interface BrushMarkOptions {
  brush: BrushId;
  color: string;
  isEraser: boolean;
  points: readonly Point[];
  size: number;
  strokeId: string;
}

type Point2 = readonly [number, number];

interface RibbonSection {
  left: Point2;
  right: Point2;
}

function circlePath(x: number, y: number, radius: number): string {
  return `M ${x - radius} ${y} a ${radius} ${radius} 0 1 0 ${radius * 2} 0 a ${radius} ${radius} 0 1 0 ${-radius * 2} 0`;
}

function rectanglePath(x: number, y: number, size: number): string {
  return `M ${x} ${y} h ${size} v ${size} h ${-size} Z`;
}

function hash(value: string): number {
  let result = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16_777_619);
  }
  return result >>> 0;
}

function random(seed: number): () => number {
  let state = seed || 1;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 4_294_967_296;
  };
}

function unitTangent(points: readonly Point[], index: number): Point2 {
  const previous = points[Math.max(0, index - 1)];
  const next = points[Math.min(points.length - 1, index + 1)];
  let x = next[0] - previous[0];
  let y = next[1] - previous[1];
  let length = Math.hypot(x, y);
  if (length === 0 && index > 0) {
    x = points[index][0] - points[index - 1][0];
    y = points[index][1] - points[index - 1][1];
    length = Math.hypot(x, y);
  }
  return length === 0 ? [1, 0] : [x / length, y / length];
}

function ribbonSections(points: readonly Point[], halfWidths: readonly number[]): RibbonSection[] {
  return points.map((point, index) => {
    const tangent = unitTangent(points, index);
    const normalX = -tangent[1];
    const normalY = tangent[0];
    const halfWidth = halfWidths[index];
    return {
      left: [point[0] + normalX * halfWidth, point[1] + normalY * halfWidth],
      right: [point[0] - normalX * halfWidth, point[1] - normalY * halfWidth],
    };
  });
}

function ribbonMarks(
  points: readonly Point[],
  halfWidths: readonly number[],
  color: string,
  roundCaps: boolean,
): BrushMark[] {
  const sections = ribbonSections(points, halfWidths);
  return sections.slice(1).map((end, offset) => {
    const pointIndex = offset + 1;
    const start = sections[pointIndex - 1];
    const endCap = roundCaps && pointIndex === sections.length - 1
      ? `A ${halfWidths[pointIndex]} ${halfWidths[pointIndex]} 0 0 0 ${end.right[0]} ${end.right[1]}`
      : `L ${end.right[0]} ${end.right[1]}`;
    const startCap = roundCaps && pointIndex === 1
      ? `A ${halfWidths[0]} ${halfWidths[0]} 0 0 0 ${start.left[0]} ${start.left[1]}`
      : `L ${start.left[0]} ${start.left[1]}`;
    return {
      element: "path",
      pointIndex,
      attributes: {
        d: `M ${start.left[0]} ${start.left[1]} L ${end.left[0]} ${end.left[1]} ${endCap} L ${start.right[0]} ${start.right[1]} ${startCap} Z`,
        fill: color,
      },
    };
  });
}

function eraserMarks(options: BrushMarkOptions): BrushMark[] {
  const { points, size } = options;
  if (points.length === 1) {
    return [{
      element: "circle",
      pointIndex: 0,
      attributes: {
        cx: String(points[0][0]),
        cy: String(points[0][1]),
        r: String(size / 2),
        fill: "black",
      },
    }];
  }
  return points.slice(1).map((point, offset) => {
    const pointIndex = offset + 1;
    const previous = points[pointIndex - 1];
    return {
      element: "path",
      pointIndex,
      attributes: {
        d: `M ${previous[0]} ${previous[1]} L ${point[0]} ${point[1]}`,
        fill: "none",
        stroke: "black",
        "stroke-width": String(size),
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
      },
    };
  });
}

function roundMarks(options: BrushMarkOptions): BrushMark[] {
  const { color, points, size } = options;
  if (points.length === 1) {
    return [{
      element: "circle",
      pointIndex: 0,
      attributes: {
        cx: String(points[0][0]),
        cy: String(points[0][1]),
        r: String(size / 2),
        fill: color,
      },
    }];
  }
  const halfWidths = points.map((point) => {
    const pressure = Math.max(0.15, point[2] ?? 0.5);
    return Math.max(0.75, size * (0.55 + pressure * 0.9) / 2);
  });
  return ribbonMarks(points, halfWidths, color, true);
}

function markerMarks(options: BrushMarkOptions): BrushMark[] {
  const { color, points, size } = options;
  if (points.length === 1) {
    return [{
      element: "path",
      pointIndex: 0,
      attributes: {
        d: rectanglePath(points[0][0] - size * 0.6, points[0][1] - size * 0.6, size * 1.2),
        fill: color,
      },
    }];
  }
  return ribbonMarks(points, points.map(() => size * 0.6), color, false);
}

function calligraphyMarks(options: BrushMarkOptions): BrushMark[] {
  const { color, points, size } = options;
  const nibX = size * 0.42;
  const nibY = size * 0.42;
  if (points.length === 1) {
    const [x, y] = points[0];
    return [{
      element: "path",
      pointIndex: 0,
      attributes: {
        d: `M ${x - nibX} ${y - nibY} L ${x + nibX} ${y + nibY}`,
        fill: "none",
        stroke: color,
        "stroke-width": String(Math.max(1, size * 0.28)),
        "stroke-linecap": "square",
      },
    }];
  }
  return points.slice(1).map((point, offset) => {
    const index = offset + 1;
    const previous = points[index - 1];
    return {
      element: "path",
      pointIndex: index,
      attributes: {
        d: `M ${previous[0] - nibX} ${previous[1] - nibY} L ${previous[0] + nibX} ${previous[1] + nibY} L ${point[0] + nibX} ${point[1] + nibY} L ${point[0] - nibX} ${point[1] - nibY} Z`,
        fill: color,
      },
    };
  });
}

function sprayMarks(options: BrushMarkOptions): BrushMark[] {
  const { color, points, size, strokeId } = options;
  const particleCount = Math.max(10, Math.min(18, Math.round(size * 1.15)));
  const spread = Math.max(5, size * 1.1);
  return points.map((point, pointIndex) => {
    const nextRandom = random(hash(`${strokeId}:${pointIndex}`));
    let path = "";
    for (let particle = 0; particle < particleCount; particle += 1) {
      const angle = nextRandom() * Math.PI * 2;
      const distance = Math.sqrt(nextRandom()) * spread;
      const radius = Math.max(0.75, size * (0.06 + nextRandom() * 0.06));
      path += `${circlePath(point[0] + Math.cos(angle) * distance, point[1] + Math.sin(angle) * distance, radius)} `;
    }
    return {
      element: "path",
      pointIndex,
      attributes: { d: path.trim(), fill: color },
    };
  });
}

function pixelMarks(options: BrushMarkOptions): BrushMark[] {
  const { color, points, size } = options;
  const cell = Math.max(3, Math.round(size * 0.8));
  const blockAt = (x: number, y: number): string => {
    const snappedX = Math.round(x / cell) * cell - cell / 2;
    const snappedY = Math.round(y / cell) * cell - cell / 2;
    return rectanglePath(snappedX, snappedY, cell);
  };
  if (points.length === 1) {
    return [{
      element: "path",
      pointIndex: 0,
      attributes: { d: blockAt(points[0][0], points[0][1]), fill: color, "shape-rendering": "crispEdges" },
    }];
  }
  return points.slice(1).map((point, offset) => {
    const pointIndex = offset + 1;
    const previous = points[pointIndex - 1];
    const distance = Math.hypot(point[0] - previous[0], point[1] - previous[1]);
    const steps = Math.max(1, Math.min(32, Math.ceil(distance / (cell * 0.7))));
    let path = "";
    for (let step = 0; step <= steps; step += 1) {
      const progress = step / steps;
      path += `${blockAt(
        previous[0] + (point[0] - previous[0]) * progress,
        previous[1] + (point[1] - previous[1]) * progress,
      )} `;
    }
    return {
      element: "path",
      pointIndex,
      attributes: { d: path.trim(), fill: color, "shape-rendering": "crispEdges" },
    };
  });
}

export function buildBrushMarks(options: BrushMarkOptions): BrushMark[] {
  if (options.isEraser) return eraserMarks(options);
  if (options.brush === "round") return roundMarks(options);
  if (options.brush === "marker") return markerMarks(options);
  if (options.brush === "calligraphy") return calligraphyMarks(options);
  if (options.brush === "spray") return sprayMarks(options);
  return pixelMarks(options);
}
